import { PropertyRepository } from 'application/repositories/PropertyRepository';
import { PostPutDeleteBaseResponse } from 'domain/entities/CommonEntity';
import { ContractResponse } from 'domain/entities/ContractEntity';
import {
    AuctionPriceLimitUpdate,
    DataListedProperty,
    DataProperty,
    DetailMemberAuctionResponse,
    EvictionListedProperty,
    ListedPropertyAssessmentDetailPayload,
    ListedPropertyAssessmentMain,
    ListedPropertyDetail,
    ListedPropertyFilter,
    ListedPropertyInquiresCreate,
    ListedPropertyRoomAssessment,
    ListedPropertyRoomGetAssessmentDetail,
    ListedPropertySalePriceLimitUpdate,
    ParamsProperty,
    PropertyDetailEntity,
    PropertyListEntity,
} from 'domain/entities/PropertyEntity';
import { SellingPriceResult } from 'presentation/pages/CU/CU-036-01/models/Property';
import { CU049SearchParams } from 'presentation/pages/CU/CU-049-01/models/Model_CU-049-01';
import { commonHelpers } from 'utilities/helpers';

import axiosConfig from 'infrastructure/config/axios';
import { UserStorage } from 'infrastructure/storages/UserStorage';

export class PropertyAPI implements PropertyRepository {
    async getListProperty(params: ParamsProperty): Promise<DataProperty[]> {
        const API_URL = '/master/property';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response.data;
    }

    async getListedPropertiesList(params: {
        page: number;
        pageSize: number;
        keyword: string;
        buyNowPriceMin?: number;
        buyNowPriceMax?: number;
        oldMin?: number;
        oldMax?: number;
        areaId?: string;
        isLogin?: number;
    }): Promise<PropertyListEntity> {
        const API_URL = '/listed-properties/list';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response.data;
    }

    async getDetailProperty(params: { propertyId: number }): Promise<PropertyDetailEntity> {
        const API_URL = '/master/property/detail';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response.data;
    }

    async getPropertyArea(params: {
        page?: number;
        pageSize: number;
        buyNowPriceMin?: number;
        buyNowPriceMax?: number;
        oldMin?: number;
        oldMax?: number;
        areaId?: number | string;
        keyword?: string;
    }): Promise<DataListedProperty[]> {
        const API_URL = '/listed-properties/list';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response.data;
    }

    async getListedPropertyDetail(params: {
        ListedPropertyId: number;
    }): Promise<ListedPropertyDetail> {
        const API_URL = '/listed-property/detail';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response.data;
    }

    async getListedPropertyRoomGetAssessmentDetail(params: {
        listedPropertyId: number;
    }): Promise<ListedPropertyRoomGetAssessmentDetail> {
        const API_URL = '/listed-property-room/get-assessment-detail';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response.data;
    }

    async putListedPropertyAssessmentDetail(
        data: ListedPropertyAssessmentDetailPayload,
    ): Promise<PostPutDeleteBaseResponse> {
        const API_URL = '/listed-property/assessment-detail';
        const response = await axiosConfig.getAxiosConfig().put(API_URL, data);
        return response.data;
    }

    async postListedPropertyInquiriesCreate(
        data: ListedPropertyInquiresCreate,
    ): Promise<PostPutDeleteBaseResponse> {
        const API_URL = '/listed-property-inquiries/create';
        const response = await axiosConfig.getAxiosConfig().post(API_URL, data);
        return response.data;
    }

    async postListedPropertyRoomAssessment(
        data: ListedPropertyRoomAssessment,
    ): Promise<PostPutDeleteBaseResponse> {
        const API_URL = '/listed-property-room/assessment';
        const formData = commonHelpers.convertObjectToFormData(data);
        const response = await axiosConfig.getAxiosConfig().post(API_URL, formData);
        return response.data;
    }

    async getListedPropertyFilter(params: {
        page?: number;
        pageSize: number;
        buyNowPriceMin?: number;
        buyNowPriceMax?: number;
        oldMin?: number;
        oldMax?: number;
        areaId?: number;
    }): Promise<ListedPropertyFilter> {
        const API_URL = '/listed-property/filter';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response.data;
    }

    async getMasterPropertyFilterArea(params: CU049SearchParams): Promise<ListedPropertyFilter> {
        const API_URL = `/master/property/filter-area?isLogin=${params.isLogin}`;
        const formData = new FormData();
        const obj: any = params;
        for (const property in obj) {
            formData.append(property, obj[`${property}`]);
        }
        const response = await axiosConfig.getAxiosConfig().post(API_URL, formData);
        return response.data;
    }

    async getMasterPropertyFilterStation(params: CU049SearchParams): Promise<ListedPropertyFilter> {
        const API_URL = `/master/property/filter?isLogin=${params.isLogin}`;
        const formData = new FormData();
        const obj: any = params;
        for (const property in obj) {
            formData.append(property, obj[`${property}`]);
        }
        const response = await axiosConfig.getAxiosConfig().post(API_URL, formData);
        return response.data;
    }

    async putListedPropertyAssessmentMain(
        data: ListedPropertyAssessmentMain,
    ): Promise<PostPutDeleteBaseResponse> {
        const API_URL = '/listed-property/assessment-main';
        const response = await axiosConfig.getAxiosConfig().put(API_URL, data);
        return response.data;
    }

    async putListedPropertySalePriceLimitUpdate(
        data: ListedPropertySalePriceLimitUpdate,
    ): Promise<PostPutDeleteBaseResponse> {
        const API_URL = '/listed-property/sale-price-limit/update';
        const response = await axiosConfig.getAxiosConfig().put(API_URL, data);
        return response.data;
    }

    async putEvictionListedProperty(
        data: EvictionListedProperty,
    ): Promise<PostPutDeleteBaseResponse> {
        const API_URL = '/listed-property-inquiries/eviction-listed-property';
        const response = await axiosConfig.getAxiosConfig().put(API_URL, data);
        return response.data;
    }

    async cancelListedProperty(data: EvictionListedProperty): Promise<PostPutDeleteBaseResponse> {
        const API_URL = '/listed-property-inquiries/cancel-listed-property';
        const response = await axiosConfig.getAxiosConfig().put(API_URL, data);
        return response.data;
    }

    async memberBookMarkListedProperty(params: {
        listedPropertyId: number;
    }): Promise<PostPutDeleteBaseResponse> {
        const API_URL = '/member_bookmark';
        const response = await axiosConfig.getAxiosConfig().post(API_URL, params);
        return response.data;
    }

    async countLike(listedPropertyId: number): Promise<PostPutDeleteBaseResponse> {
        const API_URL = `/count-like/${listedPropertyId}`;
        const response = await axiosConfig.getAxiosConfig().get(API_URL);
        return response.data;
    }

    async updateCurrentProcess(params: {
        listedPropertyId: number;
        step: number;
    }): Promise<PostPutDeleteBaseResponse> {
        const API_URL = '/listed-property-room/update-current-process';
        const response = await axiosConfig.getAxiosConfig().put(API_URL, params);
        return response.data;
    }
    async getDetailAuctionOfMember(params: {
        listedPropertyId: number;
        isLogin?: number;
    }): Promise<DetailMemberAuctionResponse> {
        const API_URL = '/listed-property/detail-listed-property';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response.data;
    }

    async auctionPriceLimitUpdate(
        data: AuctionPriceLimitUpdate,
    ): Promise<PostPutDeleteBaseResponse> {
        const API_URL = '/auction/change-condition';
        const response = await axiosConfig.getAxiosConfig().put(API_URL, data);
        return response.data;
    }

    async getAssessmentStatus(params: { mstPropertyId: number }): Promise<any> {
        const API_URL = '/listed-property/assessment-status';
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response.data;
    }

    async getDefaultSellingPrice(params: {
        listedPropertyId: number;
    }): Promise<SellingPriceResult> {
        const API_URL = `/assessment/default-selling-price/${params.listedPropertyId}`;
        const response = await axiosConfig.getAxiosConfig().get(API_URL);
        return response.data;
    }

    async getSellingPrice(params: { auctionId: number }): Promise<any> {
        const API_URL = `/auction/selling-price/${params.auctionId}`;
        const response = await axiosConfig.getAxiosConfig().get(API_URL);
        return response.data;
    }

    async checkCancelListedProperty(params: { auctionId: number }): Promise<any> {
        const API_URL = `/auction/withdraw`;
        const response = await axiosConfig.getAxiosConfig().post(API_URL, params);
        return response;
    }
    async checkChangeSellingPrice(params: { auctionId: number }): Promise<any> {
        const API_URL = `/auction/check-condition`;
        const response = await axiosConfig.getAxiosConfig().post(API_URL, params);
        return response;
    }
    async getListPropertisDashboard(params: { tabId: number; type: number }): Promise<any> {
        const acessToken = new UserStorage().getAccessToken();
        const API_URL = `/dashboard/list-properties/?type=${params.type}&tab=${params.tabId}&isLogin=${acessToken ? '1' : '0'}`;
        const response = await axiosConfig.getAxiosConfig().get(API_URL);
        return response.data;
    }
    async getConfirmContract(params: { listedPropertyId: number }): Promise<ContractResponse> {
        const API_URL = `/setting/get-confirm-contract`;
        const response = await axiosConfig.getAxiosConfig().get(API_URL, { params });
        return response.data;
    }

    async confirmContract(payload: { agencyContractId: number }): Promise<any> {
        const API_URL = `/setting/confirm-contract`;
        const response = await axiosConfig.getAxiosConfig().put(API_URL, payload);
        return response.data;
    }
    async processBeforePeriod(payload: {
        auctionId: string;
        type: string;
        askingSalePriceUpperLimit: number;
        askingSalePriceLowerLimit: number;
        listedPropertyId: number;
        dealId: number;
    }): Promise<any> {
        const API_URL = `/dashboard/process-before-period`;
        const response = await axiosConfig.getAxiosConfig().post(API_URL, payload);
        return response;
    }
}
